import { type FC, useState, useMemo } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { useQuery } from '@tanstack/react-query';
import {
  fetchNaturalDisasters,
  DISASTER_ICONS,
  DISASTER_LABELS,
  type DisasterType,
  type NaturalDisasterEvent,
} from '@/services/natural-disasters';
import type { ThreatLevel } from '@/types';

// ---------------------------------------------------------------------------
// Natural Disaster Panel — unified view: wildfire, earthquake, volcano,
// hurricane, typhoon, tornado, flood, tsunami, landslide, blizzard,
// heatwave, solar-storm, drought
// ---------------------------------------------------------------------------

type FilterType = 'all' | DisasterType;

const SEV_COLOR: Record<ThreatLevel, string> = {
  critical: 'text-red-400 bg-red-950/30 border-red-900/50',
  high:     'text-orange-400 bg-orange-950/20 border-orange-900/50',
  medium:   'text-yellow-400 bg-yellow-950/20 border-yellow-900/40',
  low:      'text-emerald-400 bg-emerald-950/20 border-emerald-900/40',
  info:     'text-slate-400 bg-slate-900/20 border-slate-800/40',
};

const SEV_DOT: Record<ThreatLevel, string> = {
  critical: 'bg-red-500 animate-pulse',
  high:     'bg-orange-500',
  medium:   'bg-yellow-500',
  low:      'bg-emerald-500',
  info:     'bg-slate-500',
};

function formatTimeAgo(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60_000);
  const h = Math.floor(diff / 3600_000);
  const d = Math.floor(diff / 86400_000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

const FILTER_GROUPS: { label: string; types: DisasterType[] }[] = [
  { label: '🔥 Fire',    types: ['wildfire'] },
  { label: '🌍 Seismic', types: ['earthquake', 'tsunami', 'volcano'] },
  { label: '🌀 Storm',   types: ['hurricane', 'typhoon', 'tornado', 'blizzard'] },
  { label: '🌊 Water',   types: ['flood'] },
  { label: '☀️ Other',  types: ['heatwave', 'solar-storm', 'drought', 'landslide'] },
];

const DisasterCard: FC<{ event: NaturalDisasterEvent; isExpanded: boolean; onToggle: () => void }> = ({
  event, isExpanded, onToggle,
}) => (
  <button
    onClick={onToggle}
    className={clsx(
      'w-full text-left px-3 py-2 border-b border-surface-border/40 hover:bg-surface-hover transition-colors',
      SEV_COLOR[event.severity],
    )}
  >
    <div className="flex items-start gap-2">
      <span className={clsx('w-1.5 h-1.5 rounded-full mt-1.5 shrink-0', SEV_DOT[event.severity])} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-1">
          <span className="text-[10px] font-semibold leading-tight text-slate-200 truncate">
            {DISASTER_ICONS[event.type]} {event.title}
          </span>
          <span className="text-[8px] text-slate-600 shrink-0 mt-0.5">
            {formatTimeAgo(event.timestamp)}
          </span>
        </div>

        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-slate-500">{DISASTER_LABELS[event.type]}</span>
          {event.magnitude != null && (
            <span className="text-[9px] font-mono text-slate-400">
              {event.magnitude}{event.unit ?? ''}
            </span>
          )}
          {event.country && (
            <span className="text-[9px] text-slate-500 truncate">· {event.country}</span>
          )}
        </div>

        {isExpanded && event.detail && (
          <div className="mt-1.5 pt-1.5 border-t border-surface-border/40 text-[9px] text-slate-400 leading-relaxed">
            {event.detail}
            <div className="mt-1 text-[8px] text-slate-600">Source: {event.source}</div>
          </div>
        )}
      </div>
    </div>
  </button>
);

export const NaturalDisasterPanel: FC = () => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  // Live EONET + static data
  const { data: disasters = [] } = useQuery({
    queryKey: ['natural-disasters'],
    queryFn: fetchNaturalDisasters,
    refetchInterval: 10 * 60_000,
    staleTime: 5 * 60_000,
  });

  // Also merge existing earthquake + wildfire store data
  const earthquakes = useAppStore((s) => s.earthquakes);
  const wildfires   = useAppStore((s) => s.wildfires);

  const combined = useMemo<NaturalDisasterEvent[]>(() => {
    const fromEq: NaturalDisasterEvent[] = earthquakes.slice(0, 20).map((eq) => ({
      id: `eq-${eq.id}`,
      type: 'earthquake' as DisasterType,
      title: `M${eq.magnitude.toFixed(1)} — ${eq.place}`,
      lat: eq.lat,
      lon: eq.lon,
      severity: eq.magnitude >= 7 ? 'critical' : eq.magnitude >= 6 ? 'high' : eq.magnitude >= 5 ? 'medium' : 'low',
      timestamp: eq.timestamp,
      source: 'USGS',
      magnitude: eq.magnitude,
      unit: 'Mw',
      detail: `Depth: ${eq.depth} km${eq.tsunami ? ' · ⚠️ Tsunami warning issued' : ''}`,
    }));

    const fromWf: NaturalDisasterEvent[] = wildfires.slice(0, 10).map((wf) => ({
      id: `wf-${wf.id}`,
      type: 'wildfire' as DisasterType,
      title: `Active Wildfire${wf.country ? ` — ${wf.country}` : ''}`,
      lat: wf.lat,
      lon: wf.lon,
      severity: wf.frp > 1000 ? 'high' : wf.frp > 300 ? 'medium' : 'low',
      timestamp: wf.timestamp,
      source: wf.satellite,
      magnitude: Math.round(wf.frp),
      unit: 'MW FRP',
      country: wf.country,
    }));

    // Deduplicate against EONET results
    const existingIds = new Set(disasters.map((d) => d.id));
    const merged = [
      ...disasters,
      ...fromEq.filter((e) => !existingIds.has(e.id)),
      ...fromWf.filter((e) => !existingIds.has(e.id)),
    ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return merged;
  }, [disasters, earthquakes, wildfires]);

  // Count by severity
  const criticalCount = combined.filter((e) => e.severity === 'critical').length;
  const highCount     = combined.filter((e) => e.severity === 'high').length;

  const filtered = useMemo(() => {
    if (filter === 'all') return combined;
    const group = FILTER_GROUPS.find((g) => g.types.includes(filter as DisasterType));
    if (group) return combined.filter((e) => group.types.includes(e.type));
    return combined.filter((e) => e.type === filter);
  }, [combined, filter]);

  return (
    <div className="flex flex-col h-full bg-surface-panel/80 border border-surface-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-surface-border shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">
            🌋 Natural Disasters
          </span>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-surface-panel border border-surface-border text-slate-400">
            {combined.length}
          </span>
          {criticalCount > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-950/60 text-red-400 border border-red-900/50 animate-pulse">
              {criticalCount} CRITICAL
            </span>
          )}
          {highCount > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-950/40 text-orange-400 border border-orange-900/40">
              {highCount} HIGH
            </span>
          )}
        </div>
        <span className="text-[8px] text-slate-600">EONET · USGS · NHC · NOAA</span>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 px-2 py-1.5 border-b border-surface-border shrink-0 overflow-x-auto scrollbar-none">
        <button
          onClick={() => setFilter('all')}
          className={clsx(
            'shrink-0 px-2 py-0.5 rounded text-[9px] font-bold transition-colors',
            filter === 'all'
              ? 'bg-blue-900/60 text-blue-300 border border-blue-700/50'
              : 'text-slate-500 hover:text-slate-300 hover:bg-surface-hover',
          )}
        >
          All ({combined.length})
        </button>
        {FILTER_GROUPS.map((grp) => {
          const count = combined.filter((e) => grp.types.includes(e.type)).length;
          if (count === 0) return null;
          const isActive = grp.types.some((t) => t === filter);
          return (
            <button
              key={grp.label}
              onClick={() => setFilter(grp.types[0])}
              className={clsx(
                'shrink-0 px-2 py-0.5 rounded text-[9px] font-bold transition-colors',
                isActive
                  ? 'bg-blue-900/60 text-blue-300 border border-blue-700/50'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-surface-hover',
              )}
            >
              {grp.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {filtered.length === 0 && (
          <div className="flex items-center justify-center h-20 text-slate-600 text-[11px]">
            No active events
          </div>
        )}
        {filtered.map((ev) => (
          <DisasterCard
            key={ev.id}
            event={ev}
            isExpanded={expanded === ev.id}
            onToggle={() => setExpanded(expanded === ev.id ? null : ev.id)}
          />
        ))}
      </div>
    </div>
  );
};
